/* =============================================================================
 * Instalar o Futigol como app.
 *
 * O app JA' ERA instalavel antes deste arquivo - havia manifesto, icones e
 * service worker (ver public/manifest.webmanifest e src/sw.js). O que faltava
 * era o app SABER disso: quem nao conhece a "mini-infobar" do Chrome nem o menu
 * Compartilhar do iPhone nunca descobria que dava para tirar o Futigol da aba do
 * navegador. Este modulo e' a metade de produto do que ja' existia de tecnica.
 *
 * DUAS REALIDADES, e nao uma so' com excecoes:
 *
 *   navegador com convite ... Chrome, Edge, Samsung e cia disparam
 *                             `beforeinstallprompt`. O evento e' GUARDADO e o
 *                             banner nativo e' suprimido (preventDefault): quem
 *                             escolhe a hora de convidar e' o app, e nao uma
 *                             barra que aparece no meio da leitura de um jogo.
 *   navegador sem convite ... iPhone (todo navegador ali e' WebKit) e Firefox.
 *                             Nao ha' API nenhuma - a instalacao e' um caminho
 *                             de menu, e a unica coisa que o app pode fazer e'
 *                             ENSINAR o caminho certo daquele aparelho.
 *
 * O EVENTO VALE UMA VEZ SO'. Depois de `prompt()` ele esta' gasto; guardar de
 * novo nao adianta. Por isso `instalar()` limpa a referencia antes de usar - o
 * segundo clique num botao que ficou na tela deve dizer "indisponivel", e nao
 * estourar um `InvalidStateError` no console.
 *
 * ESTAR INSTALADO NAO E' UM ESTADO QUE DE' PARA CONSULTAR. Nao existe
 * `navigator.isInstalled`. O que da' para saber e':
 *   1. se ESTA JANELA e' a do app (display-mode), o que so' vale enquanto a
 *      pessoa esta' dentro dele - no navegador a resposta e' sempre "nao";
 *   2. se o evento `appinstalled` ja' passou por aqui alguma vez (gravado).
 * A gravacao pode envelhecer (a pessoa desinstala e o app nao fica sabendo), e
 * por isso ela e' DESFEITA quando um `beforeinstallprompt` novo chega: se o
 * navegador voltou a oferecer a instalacao, e' porque nao ha' o que instalar.
 *
 * Mora em shared/ ao lado de offline/ pelo mesmo motivo dele: e' estado do
 * APARELHO, nao da conta. O Futigol e' publico, e quem instala o app na tela do
 * celular nao precisa ter usuario.
 * ========================================================================== */

import { persistente } from '@shared/lib/armazenamento.js';
import { emitir, EVENTOS } from '@shared/lib/eventos.js';
import { config } from '@shared/lib/config.js';

const CHAVE = 'instalacao';

//O NAO DO NAVEGADOR nao e' o nao da pessoa: fechar o dialogo do Chrome no X ou
//no Esc e' o que mais acontece, e nao da' para distinguir de uma recusa pensada.
//Por isso ele cala o convite por um mes em vez de para sempre - intervalo em que
//a pessoa provavelmente ja' esqueceu que recusou, e curto o bastante para
//alcancar quem so' passou a usar o app todo domingo. O nao DELA, o do botao do
//card, e' outra coisa - ver `recusarConvite`.
const DIAS_ATE_CONVIDAR_DE_NOVO = 30;
const MS_POR_DIA = 86_400_000;

const estado = {
    //O `beforeinstallprompt` guardado. Sua presenca E' a resposta de "da' para
    //instalar agora com um clique" - nao ha' outra forma de saber.
    convite: null,
    instalado: false,
    dispensadoEm: 0,
    //A pessoa DISSE que nao quer o app neste aparelho. Sem prazo: ver
    //`recusarConvite`.
    recusado: false,
    iniciado: false,
};

/**
 * Comeca a ouvir o navegador. Chamado no INICIO do boot (app/main.js).
 *
 * A hora importa: `beforeinstallprompt` dispara uma unica vez por carga, e quem
 * nao estiver ouvindo quando ele passar perde o convite ate' a proxima visita.
 * Por isso ele nao espera pelo shell nem pela sessao - nada aqui depende de rede.
 */
export function iniciarInstalacao() {
    if (estado.iniciado) return;
    estado.iniciado = true;

    const salvo = persistente.ler(CHAVE, null) ?? {};
    estado.dispensadoEm = Number(salvo.dispensadoEm) || 0;
    estado.recusado = salvo.recusado === true;
    estado.instalado = emJanelaDeApp() || salvo.instalado === true;

    globalThis.addEventListener?.('beforeinstallprompt', (evento) => {
        //Sem isto o Chrome mostra a barra dele por cima do app. O convite e'
        //nosso agora - ver /usuario/instalar e o card da home.
        evento.preventDefault();
        estado.convite = evento;
        //O navegador so' oferece o que ainda nao existe: se ele esta' oferecendo,
        //a marca de "instalado" que temos gravada e' de uma instalacao que a
        //pessoa ja' removeu.
        if (estado.instalado && !emJanelaDeApp()) gravar({ instalado: false });
        emitir(EVENTOS.INSTALACAO_ALTERADA, resumo());
    });

    globalThis.addEventListener?.('appinstalled', () => {
        //Pode ter sido pelo nosso botao ou pelo menu do navegador - os dois
        //terminam aqui, e e' o unico aviso confiavel de que deu certo.
        estado.convite = null;
        gravar({ instalado: true });
        emitir(EVENTOS.INSTALACAO_ALTERADA, resumo());
    });
}

function gravar(mudanca) {
    Object.assign(estado, mudanca);
    persistente.gravar(CHAVE, { instalado: estado.instalado, dispensadoEm: estado.dispensadoEm, recusado: estado.recusado });
}

/* ---- leitura ------------------------------------------------------------ */

/** Esta janela E' o app instalado (e nao uma aba do navegador). */
export function emJanelaDeApp() {
    try {
        //`standalone` e' o iPhone, que nao implementa display-mode; os outros tres
        //modos sao janelas de app com molduras diferentes - todas sao "instalado".
        if (globalThis.navigator?.standalone === true) return true;
        return ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay'].some((modo) => globalThis.matchMedia?.(`(display-mode: ${modo})`)?.matches === true);
    } catch {
        return false;
    }
}

/** Ja' foi instalado neste aparelho - por esta janela ou por uma visita anterior. */
export function appInstalado() {
    return emJanelaDeApp() || estado.instalado === true;
}

/** Ha' convite guardado: `instalar()` resolve tudo num clique. */
export function podeInstalarDireto() {
    return estado.convite !== null && !appInstalado();
}

/**
 * O convite DISCRETO, o da home.
 *
 * DOIS CASOS, e o segundo e' o que importa mais: onde ha' clique disponivel, e no
 * IPHONE - o unico aparelho em que instalar e' comum e o navegador nunca oferece
 * nada. Sem esta segunda porta, quem usa o Futigol no iPhone so' descobriria que
 * da' para instalar entrando na conta e abrindo o perfil.
 *
 * O que o card faz nos dois e' diferente: no primeiro ele instala, no segundo ele
 * leva para a tela que ensina o caminho. No computador sem convite ele nao
 * aparece - ali o icone na barra de endereco ja' esta' na tela, e um cartao no
 * meio da lista de jogos seria interrupcao.
 *
 * Some para sempre quando a pessoa recusa, e por um mes quando quem recusou foi
 * o dialogo do navegador.
 */
export function podeConvidar() {
    if (appInstalado()) return false;
    //ANTES DE QUALQUER OUTRA REGRA, inclusive da de desenvolvimento: as demais
    //sao palpite do app sobre o aparelho, e esta e' a pessoa respondendo. Um
    //convite que volta depois do nao nao e' convite, e' insistencia.
    if (estado.recusado) return false;

    //EM DESENVOLVIMENTO O CARD APARECE SEMPRE. Quem trabalha nele esta' num
    //computador, e o computador e' justamente o caso em que ele nao aparece -
    //trabalhar as cegas, publicando para ver, era o custo. O que ele ignora e' o
    //palpite: o aparelho e o silencio de um mes (um dialogo fechado sem querer
    //escondia o card pelos trinta dias seguintes). Depois de clicar em "Não,
    //obrigado" o card some tambem aqui - para traze-lo de volta, apague
    //`futigol.instalacao` do armazenamento local.
    if (emDesenvolvimento()) return true;

    const modo = roteiroDeInstalacao().modo;
    if (modo !== 'clique' && !(aparelho().ios && modo === 'passos')) return false;

    if (!estado.dispensadoEm) return true;
    return Date.now() - estado.dispensadoEm > DIAS_ATE_CONVIDAR_DE_NOVO * MS_POR_DIA;
}

/**
 * `npm run dev` E a chave ModoDesenvolvedor ligada - as duas, e nao uma.
 *
 * A do build sozinha valeria tambem nos testes; a de config.js sozinha valeria
 * num ambiente publicado que a tenha ligada para ver os SQLs. O que existe aqui
 * e' so' a maquina de quem esta' escrevendo a tela.
 */
function emDesenvolvimento() {
    return import.meta.env?.DEV === true && config.modoDesenvolvedor;
}

/** O silencio com prazo - a recusa que veio do dialogo do navegador. */
export function dispensarConvite() {
    gravar({ dispensadoEm: Date.now() });
    emitir(EVENTOS.INSTALACAO_ALTERADA, resumo());
}

/**
 * A RECUSA DA PESSOA, gravada no aparelho e SEM PRAZO - o botao do card da home.
 *
 * Instalar e' decisao do aparelho, e nao da conta: quem nao quer o icone no
 * celular do trabalho nao muda de ideia em trinta dias, e o convite voltando
 * sozinho e' a definicao de propaganda chata. Por isso este nao e' definitivo,
 * enquanto o do dialogo do navegador (`dispensarConvite`) continua valendo um
 * mes - la' nao da' para saber se foi um nao ou um Esc.
 *
 * NAO E' UM BECO SEM SAIDA: quem mudar de ideia continua achando a instalacao no
 * perfil, em /usuario/instalar - aquela tela nunca dependeu deste convite.
 * Instalar de fato ali derruba a marca pelo `appinstalled`.
 *
 * Fica no MESMO registro do resto (`futigol.instalacao`), que sobrevive ao
 * logout de proposito - ver limparTudo() em lib/armazenamento.js. Sair da conta
 * nao muda de aparelho.
 */
export function recusarConvite() {
    gravar({ recusado: true, dispensadoEm: Date.now() });
    emitir(EVENTOS.INSTALACAO_ALTERADA, resumo());
}

function resumo() {
    return { instalado: appInstalado(), podeInstalarDireto: podeInstalarDireto(), emJanelaDeApp: emJanelaDeApp() };
}

/* ---- acao --------------------------------------------------------------- */

/**
 * Abre o dialogo do navegador. Precisa ser chamado DENTRO do clique - o Chrome
 * exige gesto do usuario e recusa a chamada solta.
 *
 * @returns {Promise<'aceito'|'recusado'|'indisponivel'|'falhou'>}
 */
export async function instalar() {
    const convite = estado.convite;
    if (!convite) return 'indisponivel';

    //Some ANTES do await: o evento nao serve duas vezes, e um segundo clique
    //enquanto o dialogo esta' aberto pediria a mesma coisa de novo.
    estado.convite = null;
    emitir(EVENTOS.INSTALACAO_ALTERADA, resumo());

    try {
        await convite.prompt();
        const { outcome } = await convite.userChoice;
        if (outcome === 'accepted') {
            //`appinstalled` costuma vir logo depois e grava de novo - aqui e' para
            //a tela nao ficar meio segundo dizendo "instale" depois do sim.
            gravar({ instalado: true });
            emitir(EVENTOS.INSTALACAO_ALTERADA, resumo());
            return 'aceito';
        }
        //Recusar tambem e' resposta: o convite da home sai de cena pelo mesmo
        //prazo do "agora nao".
        dispensarConvite();
        return 'recusado';
    } catch (erro) {
        console.warn('[instalacao] o navegador recusou o convite', erro);
        return 'falhou';
    }
}

/* ---- o aparelho e o caminho dele ---------------------------------------- */

/**
 * Sistema e navegador, deduzidos do user agent.
 *
 * User agent e' palpite, e aqui isso e' aceitavel: o pior caso de errar e'
 * mostrar o passo a passo de outro navegador - texto, nao comportamento. Nenhuma
 * decisao funcional depende disto (quem decide se da' para instalar num clique e'
 * o `beforeinstallprompt`, que e' fato).
 */
export function aparelho() {
    const ua = globalThis.navigator?.userAgent ?? '';
    //O iPad com iPadOS 13+ se anuncia como Macintosh; o que o entrega e' a tela
    //sensivel ao toque.
    const ios = /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && (globalThis.navigator?.maxTouchPoints ?? 0) > 1);
    const android = /android/i.test(ua);

    return { ios, android, desktop: !ios && !android, navegador: navegadorDe(ua, ios) };
}

function navegadorDe(ua, ios) {
    //No iPhone TODO navegador e' o WebKit por baixo, e cada um tem um nome
    //proprio no UA. A ordem importa: o Chrome de iOS ("CriOS") tambem casa com
    //"Safari" mais adiante na string.
    if (ios) {
        if (/crios/i.test(ua)) return 'chrome';
        if (/fxios/i.test(ua)) return 'firefox';
        if (/edgios/i.test(ua)) return 'edge';
        return 'safari';
    }
    if (/edg\//i.test(ua)) return 'edge';
    if (/samsungbrowser/i.test(ua)) return 'samsung';
    if (/opr\/|opera/i.test(ua)) return 'opera';
    if (/firefox/i.test(ua)) return 'firefox';
    if (/chrome|chromium/i.test(ua)) return 'chrome';
    if (/safari/i.test(ua)) return 'safari';
    return 'outro';
}

/**
 * O que a tela de instalacao deve mostrar AGORA.
 *
 *   'instalado' ... nao ha' o que fazer; a tela vira confirmacao
 *   'clique' ...... ha' convite guardado: um botao resolve
 *   'passos' ...... o caminho de menu daquele navegador, numerado
 *   'sem-jeito' ... o navegador nao instala nada; a saida e' trocar de navegador
 *
 * OS PASSOS SAO O CAMINHO REAL, com o nome que aparece no menu daquele aparelho.
 * "Adicionar a Tela de Inicio" e "Instalar app" sao o mesmo gesto com dois nomes,
 * e escrever o nome errado custa a instalacao - a pessoa procura no menu e nao
 * acha.
 */
export function roteiroDeInstalacao() {
    if (appInstalado()) return { modo: 'instalado', passos: [] };
    if (podeInstalarDireto()) return { modo: 'clique', passos: [] };

    const { ios, android, desktop, navegador } = aparelho();

    if (ios) {
        //Safari e Chrome de iOS tem o mesmo gesto, em barras diferentes. Os
        //outros dois nao oferecem o item no menu de compartilhamento - por isso a
        //saida deles e' abrir no Safari, e nao um passo a passo que nao existe.
        if (navegador === 'safari' || navegador === 'chrome') {
            return {
                modo: 'passos',
                titulo: navegador === 'safari' ? 'No Safari do seu iPhone ou iPad' : 'No Chrome do seu iPhone ou iPad',
                passos: [
                    { icone: 'enviar-saida', texto: navegador === 'safari' ? 'Toque em Compartilhar, na barra de baixo do Safari.' : 'Toque em Compartilhar, no menu do Chrome.' },
                    { icone: 'mais', texto: 'Role a lista e toque em "Adicionar à Tela de Início".' },
                    { icone: 'check', texto: 'Confirme em "Adicionar". O Futigol vira um ícone na sua tela.' },
                ],
            };
        }
        return { modo: 'sem-jeito', titulo: 'Abra o Futigol no Safari', passos: [], aviso: 'Neste iPhone, só o Safari e o Chrome adicionam um site à tela de início. Copie o endereço e abra em um deles para instalar o Futigol.' };
    }

    if (android) {
        if (navegador === 'firefox') {
            return { modo: 'passos', titulo: 'No Firefox do seu Android', passos: [{ icone: 'prancheta', texto: 'Toque no menu ⋮, no canto da barra de endereço.' }, { icone: 'mais', texto: 'Escolha "Instalar" ou "Adicionar à tela inicial".' }, { icone: 'check', texto: 'Confirme. O Futigol vira um ícone na sua tela.' }] };
        }
        return {
            modo: 'passos',
            titulo: navegador === 'samsung' ? 'No Samsung Internet' : 'No navegador do seu Android',
            passos: [
                { icone: 'prancheta', texto: navegador === 'samsung' ? 'Toque no menu ☰, na barra de baixo.' : 'Toque no menu ⋮, no canto da barra de endereço.' },
                { icone: 'mais', texto: navegador === 'samsung' ? 'Escolha "Adicionar página a" e depois "Tela inicial".' : 'Escolha "Instalar app" ou "Adicionar à tela inicial".' },
                { icone: 'check', texto: 'Confirme. O Futigol vira um ícone na sua tela.' },
            ],
        };
    }

    //Computador. Firefox e Safari de Mac nao instalam app web (o Safari 17+ tem
    //"Adicionar ao Dock", que e' outra coisa e nao chega aqui como convite) -
    //prometer um botao que nao existe seria pior do que dizer a verdade.
    if (desktop && (navegador === 'firefox' || navegador === 'safari')) {
        return { modo: 'sem-jeito', titulo: 'Este navegador não instala apps', passos: [], aviso: navegador === 'safari' ? 'No Mac, o Safari guarda o Futigol pelo menu Arquivo › Adicionar ao Dock. Para a instalação completa, abra no Chrome ou no Edge.' : 'O Firefox no computador não instala aplicativos da web. Abra o Futigol no Chrome ou no Edge para instalar.' };
    }

    return {
        modo: 'passos',
        titulo: 'No seu computador',
        passos: [
            { icone: 'receber-entrada', texto: 'Procure o ícone de instalar no canto direito da barra de endereço.' },
            { icone: 'prancheta', texto: 'Se ele não estiver lá, abra o menu ⋮ e escolha "Instalar Futigol".' },
            { icone: 'check', texto: 'Confirme. O Futigol abre em janela própria, sem as abas do navegador.' },
        ],
        //Chrome e Edge so' oferecem a instalacao depois de a pagina ficar um tempo
        //aberta - sem esta linha, quem nao acha o icone conclui que a tela mentiu.
        aviso: 'O ícone pode levar alguns segundos para aparecer na primeira visita.',
    };
}
